import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { get } from "../api";
import type { EvidenceRef, FindingsResponse, NewFinding } from "../api";
import { useAuth } from "../auth";
import { CoverFilter, UNASSIGNED, useCsms } from "../CoverFilter";
import { readCover, writeCover } from "../customerFilter";
import { formatDay } from "../format";
import { Time } from "../Time";

/**
 * Highlights and lowlights first raised in the last few days, across every
 * customer the caller covers.
 *
 * "New" is decided by the writer, not here: a finding the agent restated
 * from the previous report carries `carried_from` and is not sent at all
 * (migration 0017). So this page is a list of changes, where the report
 * tab is the standing picture.
 *
 * The admin-only "Covered by" is the customers page's filter, sent as the
 * same `?user=` through the same server function -- it narrows the
 * caller's scope and cannot widen it. Range, kind and grouping only
 * rearrange what was already sent.
 */

const RANGES = [1, 2, 3] as const;
type Kind = "all" | "highlight" | "lowlight";
type Group = "day" | "customer";

export default function Findings() {
  const { me } = useAuth();
  const isAdmin = me?.user.is_admin ?? false;
  const csms = useCsms(isAdmin);
  const [params, setParams] = useSearchParams();

  const days = clampDays(params.get("d"));
  const kind = (params.get("kind") as Kind) || "all";
  const group = (params.get("by") as Group) || "day";
  const user = isAdmin ? params.get("user") : null;

  // The customers page's choice carries over for an admin who arrives with
  // none in the URL, so switching pages does not silently widen the view.
  useEffect(() => {
    if (!isAdmin || params.has("user")) return;
    const remembered = readCover();
    if (remembered) setParam("user", remembered.user);
    // Once, on arrival -- not on every change of `params`.
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!user) return writeCover(null);
    const label =
      user === UNASSIGNED
        ? "Nobody covers"
        : `Covered by ${csms.find((u) => u.id === user)?.name ?? "a CSM"}`;
    writeCover({ user, label });
  }, [isAdmin, user, csms]);

  const [data, setData] = useState<FindingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Cleared first, so one person's findings are never shown under
    // another's name while the request is in flight.
    setData(null);
    setError(null);
    const q = new URLSearchParams({ days: String(days), since: startOfLocalDay(days) });
    if (user) q.set("user", user);
    void get<FindingsResponse>(`/api/findings?${q}`)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [days, user]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const all = data?.findings ?? [];
  const shown = useMemo(
    () => (kind === "all" ? all : all.filter((f) => f.kind === kind)),
    [all, kind],
  );
  const nHigh = all.filter((f) => f.kind === "highlight").length;
  const nLow = all.length - nHigh;
  const nCustomers = data?.customers.length ?? 0;
  const withNews = new Set(all.map((f) => f.customer_id)).size;

  const whose =
    user === UNASSIGNED
      ? "customers nobody covers"
      : user
        ? `${csms.find((u) => u.id === user)?.name ?? "their"}'s customers`
        : isAdmin
          ? "all customers"
          : "your customers";

  return (
    <div className="wrap wide">
      <h1>Findings</h1>
      <p className="sub">
        New highlights and lowlights across {whose}
        {data && ` (${nCustomers})`}.
      </p>

      <div className="controls" style={{ justifyContent: "space-between", marginTop: 20 }}>
        <div className="fgroup">
          <span className="dim" id="range-label">
            Added in the last
          </span>
          <div className="picker" role="group" aria-labelledby="range-label">
            {RANGES.map((n) => (
              <button
                key={n}
                type="button"
                className={days === n ? "on" : ""}
                aria-pressed={days === n}
                onClick={() => setParam("d", n === 3 ? "" : String(n))}
              >
                {n === 1 ? "1 day" : `${n} days`}
              </button>
            ))}
          </div>
          {data && (
            <span className="dim">
              Since <Time value={data.since} />
            </span>
          )}
        </div>

        <div className="fgroup">
          {isAdmin && (
            <CoverFilter value={user ?? ""} csms={csms} onChange={(v) => setParam("user", v)} />
          )}
          <span className="dim" id="group-label">
            Group by
          </span>
          <div className="picker" role="group" aria-labelledby="group-label">
            {(["day", "customer"] as const).map((g) => (
              <button
                key={g}
                type="button"
                className={group === g ? "on" : ""}
                aria-pressed={group === g}
                onClick={() => setParam("by", g === "day" ? "" : g)}
              >
                {g === "day" ? "Day" : "Customer"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error ? (
        <div className="error">{error}</div>
      ) : !data ? (
        <div className="empty">Loading…</div>
      ) : (
        <>
          <div className="kpis">
            <Kpi label="New highlights" value={nHigh} cls="pos" />
            <Kpi label="New lowlights" value={nLow} cls="neg" />
            <Kpi label="Customers with new findings" value={withNews} of={nCustomers} />
            <Kpi label="Customers with no change" value={nCustomers - withNews} />
          </div>

          <div className="tabs" role="tablist" aria-label="Finding type">
            {(
              [
                ["all", "All", all.length],
                ["highlight", "Highlights", nHigh],
                ["lowlight", "Lowlights", nLow],
              ] as const
            ).map(([k, label, n]) => (
              <button
                key={k}
                type="button"
                role="tab"
                className={`tab${kind === k ? " on" : ""}`}
                aria-selected={kind === k}
                onClick={() => setParam("kind", k === "all" ? "" : k)}
              >
                {label} <span className="count">{n}</span>
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <div className="empty">
              No new {kind === "all" ? "findings" : `${kind}s`} in the last{" "}
              {days === 1 ? "day" : `${days} days`}.
            </div>
          ) : (
            groupBy(shown, group).map((g) => (
              <section key={g.key}>
                <div className="ghead">
                  <h2>{g.label}</h2>
                  <span className="dim">{tally(g.items)}</span>
                </div>
                {g.items.map((f) => (
                  <Card key={`${f.report_id}:${f.finding_id}`} f={f} showCustomer={group === "day"} />
                ))}
              </section>
            ))
          )}
        </>
      )}
    </div>
  );
}

/** Midnight `daysBack` days ago in the reader's own timezone, as an
    instant: "1 day" at 1pm starts yesterday 00:00, not yesterday 1pm.
    `setDate` rather than subtracting milliseconds, so a DST change or a
    month boundary inside the window lands on the right midnight. */
function startOfLocalDay(daysBack: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysBack);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function clampDays(raw: string | null): number {
  const n = Number(raw);
  return RANGES.includes(n as (typeof RANGES)[number]) ? n : 3;
}

function Kpi({ label, value, of, cls }: { label: string; value: number; of?: number; cls?: string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value${cls ? ` ${cls}` : ""}`}>
        {value}
        {of !== undefined && <span className="dim"> of {of}</span>}
      </div>
    </div>
  );
}

interface Bucket {
  key: string;
  label: string;
  items: NewFinding[];
}

/** By the local day it was added, newest first; or by customer, the most
    lowlights first, since that is where a reader should look first. */
function groupBy(items: NewFinding[], by: Group): Bucket[] {
  const buckets = new Map<string, Bucket>();
  for (const f of items) {
    const key = by === "day" ? localDayKey(f.created_at) : f.customer_id;
    const label = by === "day" ? dayLabel(f.created_at) : f.customer_display_name;
    const b = buckets.get(key) ?? { key, label, items: [] };
    b.items.push(f);
    buckets.set(key, b);
  }
  const out = [...buckets.values()];
  if (by === "day") return out.sort((a, b) => (a.key < b.key ? 1 : -1));
  const lows = (b: Bucket) => b.items.filter((f) => f.kind === "lowlight").length;
  return out.sort((a, b) => lows(b) - lows(a) || a.label.localeCompare(b.label));
}

/** A sortable key for the reader's own calendar day, never the UTC one. */
function localDayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayLabel(iso: string): string {
  const key = localDayKey(iso);
  const today = localDayKey(new Date().toISOString());
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const day = formatDay(iso);
  if (key === today) return `Today · ${day}`;
  if (key === localDayKey(y.toISOString())) return `Yesterday · ${day}`;
  return day;
}

function tally(items: NewFinding[]): string {
  const h = items.filter((f) => f.kind === "highlight").length;
  const l = items.length - h;
  return [
    h && `${h} highlight${h === 1 ? "" : "s"}`,
    l && `${l} lowlight${l === 1 ? "" : "s"}`,
  ]
    .filter(Boolean)
    .join(" · ");
}

function Card({ f, showCustomer }: { f: NewFinding; showCustomer: boolean }) {
  const report = `/c/${encodeURIComponent(f.customer)}/report?r=${encodeURIComponent(f.report_id)}`;
  const evidence = f.evidence ?? [];
  return (
    <article className={`finding ${f.kind}`}>
      <div className="fhead">
        <span className={`badge ${f.kind === "highlight" ? "pos" : "neg"}`}>
          {f.kind === "highlight" ? "Highlight" : "Lowlight"}
        </span>
        {showCustomer && <span className="cust">{f.customer_display_name}</span>}
        {f.area && <span className="chip tiny">{f.area}</span>}
      </div>
      <h3>
        <Link to={report}>{f.title ?? "Untitled finding"}</Link>
      </h3>
      {f.detail && <p>{f.detail}</p>}
      <p className="meta">
        Added <Time value={f.created_at} mode="relative" /> · {f.report_date} report
        {sources(evidence).map((s) => (
          <span key={s.thread_id}>
            {" · "}
            <Link to={`${report}#thread-${encodeURIComponent(s.thread_id)}`}>{s.label}</Link>
          </span>
        ))}
      </p>
    </article>
  );
}

/** One link per cited conversation, named by where it came from. */
function sources(evidence: EvidenceRef[]): { thread_id: string; label: string }[] {
  const seen = new Set<string>();
  return evidence
    .filter((e) => !seen.has(e.thread_id) && seen.add(e.thread_id))
    .map((e) => ({ thread_id: e.thread_id, label: e.source === "email" ? "Email" : "Slack" }));
}
